import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import WriteButton from "@/components/WriteButton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { History, Loader2, RotateCcw, Store } from "lucide-react";
import { useSupervisorApproval } from "@/components/SupervisorApprovalDialog";

const keyed = (items, type) => new Map((items || []).map((item) => [
  type === "tier" ? `${item.name}|${item.min_qty || 1}` : `${item.name}|${item.pieces || 1}`,
  item,
]));

function PriceChanges({ before, after }) {
  const rows = [];
  if (Number(before?.cost || 0) !== Number(after?.cost || 0)) rows.push({ label: "Modal / Pcs", old: before?.cost, next: after?.cost });
  [["tier", "Tingkat"], ["unit", "Satuan"]].forEach(([type, prefix]) => {
    const oldMap = keyed(type === "tier" ? before?.price_tiers : before?.units, type);
    const newMap = keyed(type === "tier" ? after?.price_tiers : after?.units, type);
    new Set([...oldMap.keys(), ...newMap.keys()]).forEach((key) => {
      const oldItem = oldMap.get(key); const newItem = newMap.get(key);
      if (Number(oldItem?.price || 0) !== Number(newItem?.price || 0)) {
        const item = newItem || oldItem;
        const detail = type === "tier" ? `min ${item.min_qty || 1}` : `isi ${item.pieces || 1}`;
        rows.push({ label: `${prefix} ${item.name} · ${detail}`, old: oldItem?.price, next: newItem?.price });
      }
    });
  });
  return rows.length ? <div className="divide-y divide-ui-line rounded-md border border-ui-line bg-ui-card">
    {rows.map((row) => <div key={row.label} className="grid grid-cols-[1fr_auto] gap-2 px-2 py-1.5 text-[11px] leading-tight"><span className="text-ui-muted">{row.label}</span><span className="font-mono whitespace-nowrap"><span className="text-danger-readable">{row.old == null ? "—" : rupiah(row.old)}</span><span className="mx-1 text-ui-muted">→</span><span className="text-brand-readable">{row.next == null ? "—" : rupiah(row.next)}</span></span></div>)}
  </div> : <p className="text-xs text-ui-muted">Tidak ada selisih harga efektif.</p>;
}

export default function ProductPriceHistoryDrawer({ product, onClose, onRestored }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState("");
  const { approveAction, approvalDialog } = useSupervisorApproval();

  const load = async (signal) => {
    if (!product?.id) return;
    setLoading(true);
    try { const { data } = await api.get(`/products/${product.id}/price-history`, { signal }); setItems(data); }
    catch (error) { if (!signal?.aborted) toast.error(apiError(error)); }
    finally { if (!signal?.aborted) setLoading(false); }
  };
  useEffect(() => {
    if (!product?.id) { setItems([]); return undefined; }
    const controller = new AbortController(); load(controller.signal);
    return () => controller.abort();
    // Product ID is the request identity; load is intentionally event-local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  const restore = async (entry) => {
    if (!window.confirm(`Kembalikan harga ${product.name} ke kondisi sebelum perubahan ini?`)) return;
    let approval = {};
    if (entry.apply_all_stores) {
      try { approval = await approveAction("all_branch_price", `Kembalikan harga ${product.name} di ${entry.branches.length} cabang`); }
      catch (error) { return toast.error(apiError(error)); }
      if (approval === false) return;
    }
    setRestoring(entry.id);
    try {
      await api.post(`/products/${product.id}/price-history/${entry.id}/restore`, {}, approval);
      toast.success("Harga sebelumnya berhasil dikembalikan");
      await load(); onRestored?.();
    } catch (error) { toast.error(apiError(error)); }
    finally { setRestoring(""); }
  };

  return <>
    {approvalDialog}
    <Sheet open={!!product} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" data-testid="price-history-drawer" className="w-[92vw] max-w-[420px] sm:max-w-md bg-ui-canvas border-ui-line text-ui-text p-0 flex flex-col">
        <SheetHeader className="shrink-0 border-b border-ui-line px-4 py-3 pr-11 text-left space-y-1">
          <SheetTitle className="flex items-center gap-2 text-base text-ui-text"><History className="h-4 w-4 text-brand-readable" /> Riwayat Harga</SheetTitle>
          <SheetDescription className="text-xs text-ui-muted">{product?.name} · terbaru di atas</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-2.5 space-y-2.5">
          {loading ? <div className="py-10 flex items-center justify-center gap-2 text-ui-muted"><Loader2 className="h-5 w-5 animate-spin" /> Memuat riwayat…</div>
            : items.length === 0 ? <div className="py-10 text-center text-ui-muted"><History className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>Belum ada perubahan harga yang tercatat.</p><p className="mt-1 text-xs">Riwayat mulai tercatat dari pembaruan sistem ini.</p></div>
              : items.map((entry) => <article key={entry.id} className="rounded-lg border border-ui-line bg-ui-soft overflow-hidden">
                <div className="px-3 py-2 border-b border-ui-line"><div className="flex items-start justify-between gap-2"><div><div className="text-sm font-semibold leading-tight">{entry.action === "restore" ? "Harga dikembalikan" : entry.apply_all_stores ? "Perubahan semua cabang" : "Perubahan harga"}</div><div className="mt-0.5 text-[10px] text-ui-muted">{entry.by || "Admin"} · {new Date(entry.at).toLocaleString("id-ID")}</div></div><span className="rounded-full bg-ui-card px-1.5 py-0.5 text-[9px] text-ui-muted whitespace-nowrap">{entry.branches.length} cabang</span></div><p className="mt-1 text-[10px] leading-tight"><span className="text-ui-muted">Alasan:</span> {entry.reason || "Tanpa alasan"}</p></div>
                <div className="p-2 space-y-2">{entry.branches.map((branch) => <section key={branch.store_id} className="space-y-1"><h4 className="flex items-center gap-1 text-[11px] font-semibold"><Store className="h-3 w-3 text-brand-readable" /> {branch.store_name}</h4><PriceChanges before={branch.before} after={branch.after} /></section>)}</div>
                <div className="border-t border-ui-line px-2 py-1.5 flex justify-end"><WriteButton size="sm" data-testid={`restore-price-${entry.id}`} variant="outline" disabled={!!restoring} onClick={() => restore(entry)} className="h-7 px-2 border-warning text-warning-readable bg-warning-soft gap-1 text-[10px]"><RotateCcw className={`h-3 w-3 ${restoring === entry.id ? "animate-spin" : ""}`} /> Kembalikan Harga Ini</WriteButton></div>
              </article>)}
        </div>
      </SheetContent>
    </Sheet>
  </>;
}
